import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  ChevronIcon,
  ClearButton,
  Container,
  EmptyState,
  LoadingState,
  OptionItem,
  Panel,
  SearchInput,
  Trigger,
  TriggerActions
} from './styles';

export type SearchableSelectOption = {
  value: string;
  label: string;
  description?: string;
  searchText?: string;
};

export type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  fluid?: boolean;
  error?: boolean;
  openUpward?: boolean;
  /** Renderiza o menu em portal (evita corte por overflow em modais/popovers) */
  menuPortal?: boolean;
  noResultsMessage?: string;
  clearable?: boolean;
  filterOptions?: (
    options: SearchableSelectOption[],
    query: string
  ) => SearchableSelectOption[];
  renderOption?: (option: SearchableSelectOption, query: string) => ReactNode;
  formatSelectedLabel?: (option: SearchableSelectOption | undefined) => string;
};

type MenuPosition = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  openUpward: boolean;
};

const defaultFilter = (options: SearchableSelectOption[], query: string) => {
  const search = query.trim().toLowerCase();
  if (!search) return options;

  return options.filter(option => {
    const searchText = (option.searchText || `${option.label} ${option.description || ''}`)
      .toLowerCase();
    return searchText.includes(search);
  });
};

const defaultFormatSelectedLabel = (option: SearchableSelectOption | undefined) => {
  if (!option) return '';
  if (option.description) return `${option.label} · ${option.description}`;
  return option.label;
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value = '',
  onChange,
  onBlur,
  placeholder = 'Selecione...',
  loading = false,
  disabled = false,
  fluid = false,
  error = false,
  openUpward = true,
  menuPortal = false,
  noResultsMessage = 'Nenhum resultado encontrado',
  clearable = true,
  filterOptions = defaultFilter,
  renderOption,
  formatSelectedLabel = defaultFormatSelectedLabel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const selectedOption = options.find(option => option.value === value);
  const filteredOptions = filterOptions(options, searchQuery);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(0);
    setMenuPosition(null);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const menuMaxHeight = 240;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const shouldOpenUpward =
      openUpward || (spaceBelow < Math.min(menuMaxHeight, 160) && spaceAbove > spaceBelow);

    if (shouldOpenUpward) {
      setMenuPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(menuMaxHeight, Math.max(120, spaceAbove)),
        openUpward: true
      });
      return;
    }

    setMenuPosition({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(menuMaxHeight, Math.max(120, spaceBelow)),
      openUpward: false
    });
  }, [openUpward]);

  const openPanel = useCallback(
    (initialQuery = '') => {
      if (disabled) return;
      setIsOpen(true);
      setSearchQuery(initialQuery);
      setHighlightedIndex(0);
      setTimeout(() => {
        if (menuPortal) updateMenuPosition();
        inputRef.current?.focus();
      }, 0);
    },
    [disabled, menuPortal, updateMenuPosition]
  );

  const selectOption = useCallback(
    (option: SearchableSelectOption) => {
      onChange?.(option.value);
      close();
    },
    [close, onChange]
  );

  useLayoutEffect(() => {
    if (!isOpen || !menuPortal) return;
    updateMenuPosition();
  }, [isOpen, menuPortal, updateMenuPosition, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen || !menuPortal) return;

    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, menuPortal, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inTrigger && !inPanel) {
        close();
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close, isOpen, onBlur]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(Math.max(0, filteredOptions.length - 1));
    }
  }, [filteredOptions.length, highlightedIndex]);

  const handleInputChange = (nextValue: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    setSearchQuery(nextValue);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      event.preventDefault();
      openPanel(selectedOption?.label || '');
      return;
    }

    if (!isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) selectOption(option);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      onBlur?.();
    }
  };

  const displayValue = isOpen
    ? searchQuery
    : selectedOption
    ? formatSelectedLabel(selectedOption)
    : '';

  const defaultRenderOption = (option: SearchableSelectOption) => (
    <>
      <div style={{ fontWeight: 400, fontSize: 14, color: '#333' }}>{option.label}</div>
      {option.description && (
        <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{option.description}</div>
      )}
    </>
  );

  const panelContent = (
    <Panel
      ref={panelRef}
      openUpward={menuPortal ? Boolean(menuPosition?.openUpward) : openUpward}
      portal={menuPortal}
      data-filter-portal-menu={menuPortal ? 'true' : undefined}
      style={
        menuPortal && menuPosition
          ? {
              top: menuPosition.top,
              bottom: menuPosition.bottom,
              left: menuPosition.left,
              width: menuPosition.width,
              maxHeight: menuPosition.maxHeight
            }
          : undefined
      }
    >
      {loading ? (
        <LoadingState>Carregando...</LoadingState>
      ) : filteredOptions.length === 0 ? (
        <EmptyState>{noResultsMessage}</EmptyState>
      ) : (
        filteredOptions.map((option, index) => (
          <OptionItem
            key={option.value}
            isHighlighted={index === highlightedIndex}
            isSelected={option.value === value}
            onMouseDown={event => event.preventDefault()}
            onMouseEnter={() => setHighlightedIndex(index)}
            onClick={() => selectOption(option)}
          >
            {renderOption ? renderOption(option, searchQuery) : defaultRenderOption(option)}
          </OptionItem>
        ))
      )}
    </Panel>
  );

  return (
    <Container ref={containerRef} fluid={fluid}>
      <Trigger
        error={error}
        disabled={disabled}
        isOpen={isOpen}
        onMouseDown={event => {
          if (disabled) return;
          if ((event.target as HTMLElement).closest('button')) return;
          event.preventDefault();
          if (!isOpen) {
            openPanel('');
          } else {
            inputRef.current?.focus();
          }
        }}
      >
        <SearchInput
          ref={inputRef}
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={event => handleInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (!isOpen) onBlur?.();
          }}
        />
        <TriggerActions>
          {clearable && value && !disabled && (
            <ClearButton
              type="button"
              tabIndex={-1}
              onMouseDown={event => event.preventDefault()}
              onClick={event => {
                event.stopPropagation();
                onChange?.('');
                setSearchQuery('');
                close();
                onBlur?.();
              }}
              aria-label="Limpar seleção"
            >
              ×
            </ClearButton>
          )}
          <ChevronIcon isOpen={isOpen} />
        </TriggerActions>
      </Trigger>

      {isOpen &&
        (menuPortal
          ? menuPosition
            ? createPortal(panelContent, document.body)
            : null
          : panelContent)}
    </Container>
  );
};

export default SearchableSelect;
